import { CONSTANTS } from '../../_data/lookups.js';
import { bindMaterializedResource } from '../mutableWorkflowOverlay.js';
import { mutableStepKeyToStepPath, projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta } from '../workflowCanonicalization.js';
import { extractResourceTypeHandleFromRole } from '../workflowExtraction.js';
import { resolveWorkflowStateChain } from '../workflowStateResolution.js';
import { resolveMacroStepContextForToolStep, stampAndAppendTracePoint } from './tracePointHelpers.js';
export function resolveToolTraceContext(workflow, step) {
    return {
        stepPath: mutableStepKeyToStepPath(workflow, step.stepKey),
        macroStepContext: resolveMacroStepContextForToolStep(workflow, step.stepKey),
    };
}
export function resolveToolInputs(workflow, step) {
    const payload = {};
    for (const roleName of step.roleBindingSpec.inputBindings) {
        const resolution = resolveWorkflowStateChain(workflow.workflowState, { stepKey: step.stepKey, roleName });
        if (resolution.status === 'unresolved') {
            return { status: 'unresolved', roleName, stepKey: step.stepKey, reason: resolution.reason, path: resolution.path };
        }
        if (resolution.status === 'externalInputPotential') {
            return { status: 'externalInputPotential', roleName, stepKey: step.stepKey, path: resolution.path };
        }
        payload[roleName] = resolution.entry.projection;
    }
    return { status: 'ready', payload };
}
export function describeToolOutput(tool, roleName, projection) {
    const outputRole = tool.roleSpec.outputRoleByName[roleName];
    if (!outputRole)
        throw new Error(`Output role '${roleName}' missing in tool roleSpec`);
    const resourceTypeHandle = extractResourceTypeHandleFromRole(outputRole);
    if (!resourceTypeHandle) {
        throw new Error(`Output role '${roleName}' is missing resourceTypeHandle; tool-step outputs must be resource-backed`);
    }
    return { roleName, projection, resourceTypeHandle };
}
export function bindToolOutput(binding, step, roleName, resource) {
    return {
        workflowState: bindMaterializedResource(binding.workflowState, { stepKey: step.stepKey, roleName }, resource),
        materializedByRoleName: { ...binding.materializedByRoleName, [roleName]: resource },
    };
}
export function completeToolExecution(state, context, trace, binding) {
    const { step } = context.stepResolution;
    const { currentThreadIndex, currentStepCounter } = context.cursorContext;
    const errorRole = CONSTANTS.Cosmos.RoleNames.ErrorOutput;
    const missing = step.roleBindingSpec.outputBindings.filter((name) => name !== errorRole)
        .filter((name) => !binding.materializedByRoleName[name]);
    if (!binding.materializedByRoleName[errorRole] && missing.length > 0) {
        throw new Error(`ContractViolation: tool '${step.toolHandle}' omitted required outputs (${missing.join(', ')}) without emitting ${errorRole}`);
    }
    const updatedWorkflow = { ...state.mutableWorkflow, workflowState: binding.workflowState };
    const event = {
        tracePointKind: 'tick',
        workflowHandle: state.mutableWorkflow.handle,
        counterSnapshot: { stepCounterAfter: currentStepCounter + 1 },
        threadIndex: currentThreadIndex,
        ...trace,
        stepKind: 'tool',
        tickKind: 'tool_execution',
        delta: {
            workflowStateDelta: projectMutableWorkflowStateEntriesByStepKeyToCanonicalDelta(updatedWorkflow, step.stepKey, binding.workflowState[step.stepKey] ?? {}),
        },
    };
    return {
        mutableWorkflow: updatedWorkflow,
        stepCounters: { [currentThreadIndex]: currentStepCounter + 1 },
        currentThreadIndex,
        ...stampAndAppendTracePoint(state, currentThreadIndex, event),
    };
}
export function toolExecutionFailureTick(state, context) {
    const { currentThreadIndex, currentStepCounter } = context.cursorContext;
    const trace = resolveToolTraceContext(state.mutableWorkflow, context.stepResolution.step);
    const event = {
        tracePointKind: 'tick',
        workflowHandle: state.mutableWorkflow.handle,
        counterSnapshot: { stepCounterAfter: currentStepCounter },
        threadIndex: currentThreadIndex,
        ...trace,
        stepKind: 'tool',
        tickKind: 'tool_execution',
        delta: { workflowStateDelta: {} },
    };
    return {
        mutableWorkflow: state.mutableWorkflow,
        stepCounters: { [currentThreadIndex]: state.stepCounters[currentThreadIndex] ?? currentStepCounter },
        currentThreadIndex,
        ...stampAndAppendTracePoint(state, currentThreadIndex, event),
    };
}
